import { DOCUMENT } from "@angular/common";
import { Inject, Injectable } from "@angular/core";

import { MENU_ANCHOR_TARGETS, MENU_PANEL_SELECTOR, MENU_TRIGGER_SELECTOR, sel } from "../onboarding.const";

import { buildElementSelector, ELEMENT_ID } from "@app/core/constants/element-ids.const";
import { EOnboardingPlacement, IOnboardingStep } from "@app/shared/types/onboarding.type";

const SPOTLIGHT_PADDING = 0;
const SPOTLIGHT_PADDINGS: Record<string, number> = {
  [sel(ELEMENT_ID.CreateClient)]: 8,
  [sel(ELEMENT_ID.CreatePolicy)]: 8,
  [sel(ELEMENT_ID.CreateProduct)]: 8,
};

const TOOLTIP_GAP = 12;
const MENU_TOOLTIP_GAP = 40;
const TOOLTIP_WIDTH = 608;
const TOOLTIP_EST_HEIGHT = 220;
const TOOLTIP_SELECTOR = ".onb-tooltip";
const VIEWPORT_MARGIN = 16;
const WIDE_ELEMENT_RATIO = 0;
const LEFT_COLUMN_ID = "left-column";
const RIGHT_COLUMN_ID = "right-column";
const RECT_EPSILON = 0;

export const CENTERED_TOOLTIP_STYLE: Record<string, string> = {
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
};

export interface IOnboardingLayout {
  spotlight: Record<string, string> | null;
  blockers: Record<string, string>[] | null;
  tooltip: Record<string, string>;
}

@Injectable()
export class OnboardingPositionService {
  constructor(@Inject(DOCUMENT) private document: Document) {}

  computeLayout(rect: DOMRect | null, step: IOnboardingStep | null, target: HTMLElement | null): IOnboardingLayout {
    return {
      spotlight: this.buildSpotlightStyle(rect, step),
      blockers: this.buildBlockerStyles(rect, step),
      tooltip: this.buildTooltipStyle(rect, step, target),
    };
  }

  rectsMatch(a: DOMRect, b: DOMRect): boolean {
    return (
      Math.abs(a.top - b.top) < RECT_EPSILON &&
      Math.abs(a.left - b.left) < RECT_EPSILON &&
      Math.abs(a.width - b.width) < RECT_EPSILON &&
      Math.abs(a.height - b.height) < RECT_EPSILON
    );
  }

  private buildSpotlightStyle(rect: DOMRect | null, step: IOnboardingStep | null): Record<string, string> | null {
    if (!rect) {
      return null;
    }

    const padding = this.getSpotlightPadding(step);

    return {
      top: `${rect.top - padding}px`,
      left: `${rect.left - padding}px`,
      width: `${rect.width + padding * 2}px`,
      height: `${rect.height + padding * 2}px`,
    };
  }

  private buildBlockerStyles(rect: DOMRect | null, step: IOnboardingStep | null): Record<string, string>[] | null {
    if (!rect) {
      return null;
    }

    const padding = this.getSpotlightPadding(step);
    const top = Math.max(rect.top - padding, 0);
    const bottom = rect.bottom + padding;
    const left = Math.max(rect.left - padding, 0);
    const right = rect.right + padding;

    return [
      { top: "0", left: "0", right: "0", height: `${top}px` },
      { top: `${bottom}px`, left: "0", right: "0", bottom: "0" },
      { top: `${top}px`, left: "0", width: `${left}px`, height: `${bottom - top}px` },
      { top: `${top}px`, left: `${right}px`, right: "0", height: `${bottom - top}px` },
    ];
  }

  private getSpotlightPadding(step: IOnboardingStep | null): number {
    const selector = step?.targetSelector;

    if (!selector) {
      return SPOTLIGHT_PADDING;
    }

    return SPOTLIGHT_PADDINGS[buildElementSelector(selector)] ?? SPOTLIGHT_PADDING;
  }

  private buildTooltipStyle(
    rect: DOMRect | null,
    step: IOnboardingStep | null,
    target: HTMLElement | null,
  ): Record<string, string> {
    const preferred = step ? this.resolvePlacement(step, target) : EOnboardingPlacement.Center;

    if (!rect || preferred === EOnboardingPlacement.Center) {
      return CENTERED_TOOLTIP_STYLE;
    }

    const tooltipHeight = this.getTooltipHeight();

    if (this.isMenuStep(target)) {
      const menuStyle = this.buildMenuTooltipStyle(tooltipHeight);

      if (menuStyle) {
        return menuStyle;
      }
    }

    const placement = this.fitPlacement(rect, preferred, tooltipHeight);
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    let left: number;
    let top: number;

    switch (placement) {
      case EOnboardingPlacement.Top:
        left = centerX - TOOLTIP_WIDTH / 2;
        top = rect.top - TOOLTIP_GAP - tooltipHeight;
        break;
      case EOnboardingPlacement.Left:
        left = rect.left - TOOLTIP_GAP - TOOLTIP_WIDTH;
        top = centerY - tooltipHeight / 2;
        break;
      case EOnboardingPlacement.Right:
        left = rect.right + TOOLTIP_GAP;
        top = centerY - tooltipHeight / 2;
        break;
      case EOnboardingPlacement.Bottom:
      default:
        left = centerX - TOOLTIP_WIDTH / 2;
        top = rect.bottom + TOOLTIP_GAP;
        break;
    }

    return {
      top: `${this.clamp(top, VIEWPORT_MARGIN, viewportHeight - tooltipHeight - VIEWPORT_MARGIN)}px`,
      left: `${this.clamp(left, VIEWPORT_MARGIN, viewportWidth - TOOLTIP_WIDTH - VIEWPORT_MARGIN)}px`,
    };
  }

  private buildMenuTooltipStyle(tooltipHeight: number): Record<string, string> | null {
    const panel = this.document.querySelector<HTMLElement>(MENU_PANEL_SELECTOR);

    if (!panel) {
      return null;
    }

    const panelRect = panel.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const inRightHalf = panelRect.left + panelRect.width / 2 > viewportWidth / 2;
    const left = inRightHalf
      ? panelRect.left - MENU_TOOLTIP_GAP - TOOLTIP_WIDTH
      : panelRect.right + MENU_TOOLTIP_GAP;
    const top = panelRect.top + panelRect.height / 2 - tooltipHeight / 2;

    return {
      top: `${this.clamp(top, VIEWPORT_MARGIN, viewportHeight - tooltipHeight - VIEWPORT_MARGIN)}px`,
      left: `${this.clamp(left, VIEWPORT_MARGIN, viewportWidth - TOOLTIP_WIDTH - VIEWPORT_MARGIN)}px`,
    };
  }

  private fitPlacement(rect: DOMRect, preferred: EOnboardingPlacement, tooltipHeight: number): EOnboardingPlacement {
    const slack: Record<EOnboardingPlacement, number> = {
      [EOnboardingPlacement.Top]: rect.top - tooltipHeight - TOOLTIP_GAP - VIEWPORT_MARGIN,
      [EOnboardingPlacement.Bottom]: window.innerHeight - rect.bottom - tooltipHeight - TOOLTIP_GAP - VIEWPORT_MARGIN,
      [EOnboardingPlacement.Left]: rect.left - TOOLTIP_WIDTH - TOOLTIP_GAP - VIEWPORT_MARGIN,
      [EOnboardingPlacement.Right]: window.innerWidth - rect.right - TOOLTIP_WIDTH - TOOLTIP_GAP - VIEWPORT_MARGIN,
      [EOnboardingPlacement.Center]: Number.POSITIVE_INFINITY,
    };
    const order = this.placementOrder(rect, preferred, tooltipHeight);

    return (
      order.find(side => slack[side] >= 0) ??
      order.reduce((best, side) => (slack[side] > slack[best] ? side : best), order[0])
    );
  }

  private placementOrder(rect: DOMRect, preferred: EOnboardingPlacement, tooltipHeight: number): EOnboardingPlacement[] {
    const vertical = [EOnboardingPlacement.Top, EOnboardingPlacement.Bottom];
    const horizontal = [EOnboardingPlacement.Left, EOnboardingPlacement.Right];
    const isWide = rect.width >= window.innerWidth * WIDE_ELEMENT_RATIO;
    const isTall = rect.height >= tooltipHeight;

    let allowed: EOnboardingPlacement[];

    if (isWide) {
      allowed = vertical;
    } else if (isTall) {
      allowed = horizontal;
    } else {
      allowed = [...vertical, ...horizontal];
    }

    return [...new Set([preferred, ...allowed])].filter(side => allowed.includes(side));
  }

  private getTooltipHeight(): number {
    const tooltip = this.document.querySelector<HTMLElement>(TOOLTIP_SELECTOR);

    return tooltip?.offsetHeight || TOOLTIP_EST_HEIGHT;
  }

  private isMenuStep(target: HTMLElement | null): boolean {
    return !!target?.matches(MENU_TRIGGER_SELECTOR);
  }

  private resolvePlacement(step: IOnboardingStep, target: HTMLElement | null): EOnboardingPlacement {
    if (target) {
      if (target.closest(`#${LEFT_COLUMN_ID}`)) {
        return EOnboardingPlacement.Right;
      }

      if (target.closest(`#${RIGHT_COLUMN_ID}`)) {
        return EOnboardingPlacement.Left;
      }
    }

    const targetSelector = step.targetSelector ? buildElementSelector(step.targetSelector) : null;

    if (targetSelector) {
      const anchor = MENU_ANCHOR_TARGETS[targetSelector];

      if (anchor) {
        return anchor.placement;
      }
    }

    return step.placement;
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }
}
